import axios from 'axios'
import { Message } from 'element-ui'
import { Toast } from 'vant'

// 创建axios实例
const request = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '', // api的base_url
  timeout: 60000 // 请求超时时间
})

let loginCooldownUntil = 0
try {
  const v = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth:login-cooldown-until') : ''
  if (v) {
    const ts = Number(v)
    if (Number.isFinite(ts)) loginCooldownUntil = ts
  }
} catch (e) {
  // ignore
}
if (typeof window !== 'undefined') {
  window.addEventListener('auth:login-success', () => {
    const ts = Date.now() + 3000
    loginCooldownUntil = ts
    try {
      sessionStorage.setItem('auth:login-cooldown-until', String(ts))
    } catch (e) {
      // ignore
    }
  })
}

// 获取token的函数
function getToken() {
  return localStorage.getItem('token')
}

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const isUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi|MicroMessenger|QQBrowser|UCBrowser|baidubrowser|MiuiBrowser|HuaweiBrowser|Quark/i.test(ua)
  const isWidth = typeof window !== 'undefined' && window.innerWidth < 768
  return isUA || isWidth
}

let isErrorMessageShowing = false
let lastErrorTime = 0
function showError(msg) {
  const now = Date.now()
  if (now - lastErrorTime < 1000) return
  lastErrorTime = now
  
  if (isMobileDevice()) {
    if (isErrorMessageShowing) return
    isErrorMessageShowing = true
    Toast.fail({
      message: msg,
      duration: 3000,
      onClose: () => {
        isErrorMessageShowing = false
      }
    })
  } else {
    if (isErrorMessageShowing) return
    isErrorMessageShowing = true
    Message({
      message: msg,
      type: 'error',
      duration: 5 * 1000,
      onClose: () => {
        isErrorMessageShowing = false
      }
    })
  }
}

function getLoginRedirectPath() {
  if (typeof navigator === 'undefined') return '/'
  if (isMobileDevice()) {
    const currentUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : ''
    return currentUrl ? `/mobile-login?redirect=${encodeURIComponent(currentUrl)}` : '/mobile-login'
  }
  return '/'
}

let lastAuthRequiredAt = 0
function triggerLoginRequired() {
  if (typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastAuthRequiredAt < 800) return
  lastAuthRequiredAt = now

  try {
    sessionStorage.setItem('auth:login-required', '1')
  } catch (e) {
    // ignore
  }

  const event = new CustomEvent('auth:login-required', {
    detail: {
      redirect: window.location.pathname + window.location.search + window.location.hash
    }
  })
  window.dispatchEvent(event)
}

function handleUnauthorized(config) {
  if (Date.now() < loginCooldownUntil) {
    return
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
  }

  // Whitelist for APIs that should not trigger login dialog on 401
  // This prevents login popup on homepage refresh if these APIs return 401 for guests
  if (config && config.url) {
    const whitelist = [
      '/api/frontend/video-recommend/list',
      '/api/frontend/content/homepage-recommend',
      '/api/frontend/certificate-category/violation-list',
      '/api/frontend/course/'
    ]
    if (whitelist.some(u => config.url.includes(u))) {
      return
    }
  }

  const redirectPath = getLoginRedirectPath()
  if (redirectPath.startsWith('/mobile-login')) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/mobile-login')) {
      window.location.href = window.location.origin + redirectPath
    }
    return
  }

  triggerLoginRequired()
}

// request拦截器
request.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    // 添加token到请求头
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 处理请求错误
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// response拦截器
request.interceptors.response.use(
  response => {
    const res = response.data
    
    // 统一处理响应
    if (res.code !== 0 && res.code !== 200) {
      // 处理token过期或无效的情况
      if (res.code === 401) {
        handleUnauthorized(response.config)
        // 不返回错误，避免弹出提示
        return Promise.resolve()
      }
      
      // 协议模板接口不显示错误提示
      const isAgreementTemplate = response.config && response.config.url && response.config.url.includes('/api/frontend/agreement/template')
      const isCourseDetail = response.config && response.config.url && /^\/api\/frontend\/course\/[^/]+$/.test(response.config.url)
      const isErrorWhitelisted = response.config && response.config.url && (
        response.config.url.includes('/api/frontend/course/list') ||
        response.config.url.includes('/api/frontend/home/hot-course-list') ||
        response.config.url.includes('/api/frontend/personal/grade-query') ||
        response.config.url.includes('/api/frontend/certificate-category/certificate-query') ||
        response.config.url.includes('/api/frontend/certificate-category/violation-list') ||
        isCourseDetail
      )
      const skipErrorMessage = response.config && response.config.skipErrorMessage
      if (!isAgreementTemplate && !isErrorWhitelisted && !skipErrorMessage) {
        const msg = res.message || '未知错误'
        console.error('请求失败:', msg)
        showError(msg)
      }
      return Promise.reject(new Error(res.message || '未知错误'))
    } else {
      return res
    }
  },
  error => {
    // 处理响应错误
    console.error('响应错误:', error)
    
    // 处理网络错误或服务器错误
    if (error.response) {
      // 服务器响应了错误状态码
      if (error.response.status === 401) {
        handleUnauthorized(error.config)
        // 不返回错误，避免弹出提示
        return Promise.resolve()
      }
    }
    
    // 协议模板接口不显示错误提示
    const isAgreementTemplate = error.config && error.config.url && error.config.url.includes('/api/frontend/agreement/template')
    const isCourseDetail = error.config && error.config.url && /^\/api\/frontend\/course\/[^/]+$/.test(error.config.url)
    const isErrorWhitelisted = error.config && error.config.url && (
      error.config.url.includes('/api/frontend/course/list') ||
      error.config.url.includes('/api/frontend/home/hot-course-list') ||
      error.config.url.includes('/api/frontend/personal/grade-query') ||
      error.config.url.includes('/api/frontend/certificate-category/certificate-query') ||
      error.config.url.includes('/api/frontend/certificate-category/violation-list') ||
      isCourseDetail
    )
    if (!isAgreementTemplate && !isErrorWhitelisted) {
      showError(error.message || '网络请求失败')
    }
    return Promise.reject(error)
  }
)

export default request
